import { useState, type FormEvent } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/components/feature/AuthContext";

const demoAccounts = [
  { email: "admin@crm.com", password: "admin123", role: "Super Admin", name: "Rajesh Kumar" },
  { email: "manager@crm.com", password: "manager123", role: "Sales Manager", name: "Priya Sharma" },
  { email: "exec@crm.com", password: "exec123", role: "Sales Executive", name: "Amit Patel" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedDemo, setExpandedDemo] = useState(false);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/dashboard";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);
    const result = await login(email.trim(), password);
    setLoading(false);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error || "Login failed");
    }
  };

  const fillDemo = (demoEmail: string, demoPassword: string) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
    setError("");
    setExpandedDemo(false);
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Left Panel — Branding */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] relative bg-sidebar-bg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sidebar-bg via-sidebar-bg to-sidebar-hover" />
        <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_20%_60%,rgba(255,255,255,0.6)_0%,transparent_60%)]" />
        <div className="absolute top-16 left-16 w-72 h-72 rounded-full bg-indigo-600/10 blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-indigo-400/5 blur-3xl" />

        <div className="relative z-10 flex flex-col justify-between w-full p-14 xl:p-20">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
                <i className="ri-pie-chart-2-fill text-xl text-white" />
              </div>
              <span className="text-2xl font-bold tracking-tight text-white">CRM Pro</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed max-w-sm mt-8">
              Streamline your sales pipeline, nurture leads, and close deals faster with the most intuitive CRM platform.
            </p>
          </div>

          <div className="space-y-8">
            <div className="grid grid-cols-2 gap-5">
              <div className="bg-sidebar-card border border-sidebar-border rounded-xl p-5">
                <p className="text-3xl font-bold text-white">10K+</p>
                <p className="text-xs text-gray-400 mt-1">Active Users</p>
              </div>
              <div className="bg-sidebar-card border border-sidebar-border rounded-xl p-5">
                <p className="text-3xl font-bold text-white">98%</p>
                <p className="text-xs text-gray-400 mt-1">Satisfaction Rate</p>
              </div>
              <div className="bg-sidebar-card border border-sidebar-border rounded-xl p-5">
                <p className="text-3xl font-bold text-white">2.5M</p>
                <p className="text-xs text-gray-400 mt-1">Deals Closed</p>
              </div>
              <div className="bg-sidebar-card border border-sidebar-border rounded-xl p-5">
                <p className="text-3xl font-bold text-white">24/7</p>
                <p className="text-xs text-gray-400 mt-1">Support</p>
              </div>
            </div>

            <blockquote className="border-l-2 border-indigo-500/40 pl-4">
              <p className="text-sm text-gray-400 italic leading-relaxed">
                "CRM Pro transformed how our sales team operates. We saw a 40% increase in closed deals within the first quarter."
              </p>
              <p className="text-xs text-gray-500 mt-2">— Vikram Mehta, VP Sales at TechVentures</p>
            </blockquote>
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-10">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
                <i className="ri-pie-chart-2-fill text-lg text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight text-gray-900">CRM Pro</span>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">Welcome back</h1>
            <p className="text-sm text-gray-500 mt-1.5">Sign in to your account to continue</p>
          </div>

          {error && (
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-amber-50 border border-amber-300 text-amber-800 text-sm mb-6">
              <i className="ri-error-warning-fill text-base" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email address
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors p-1"
                  title={showPassword ? "Hide password" : "Show password"}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={showPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors whitespace-nowrap">
                  Remember me
                </span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors whitespace-nowrap"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] whitespace-nowrap"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign in
                  <i className="ri-arrow-right-line" />
                </>
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-6">
            <button
              onClick={() => setExpandedDemo(!expandedDemo)}
              className="flex items-center justify-between w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <span>Demo credentials</span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${expandedDemo ? "rotate-180" : ""}`} />
            </button>
            {expandedDemo && (
              <div className="mt-3 space-y-2">
                {demoAccounts.map((demo) => (
                  <button
                    key={demo.email}
                    onClick={() => fillDemo(demo.email, demo.password)}
                    className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-indigo-50 transition-colors text-left group"
                  >
                    <div>
                      <p className="text-sm font-semibold text-indigo-600">{demo.role}</p>
                      <p className="text-xs text-gray-500">{demo.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{demo.email}</p>
                    </div>
                    <i className="ri-arrow-right-line text-indigo-300 group-hover:text-indigo-500 transition-colors" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}